import { UTCDate } from '@date-fns/utc';
import { endOfMonth, startOfMonth, subMonths } from 'date-fns';
import { AccountView } from '../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../types/supported-country-currency';
import { getAmountWithCurrency } from '../../../utils/get-amount-with-currency';
import { ValueTone } from '../components/metrics/metric-card';
import { getMonthlyBurnData } from './get-monthly-burn-data';

type AverageMonthlyBurnDataInput = {
  numberOfMonths: number;
  accounts: AccountView[];
  baseCurrency: SupportedCountryCurrency;
};

type TrendDirection = 'increasing' | 'decreasing' | 'default';

const EPSILON = 0.01;

export const getAverageMonthlyBurnData = ({ numberOfMonths, accounts, baseCurrency }: AverageMonthlyBurnDataInput) => {
  if (!accounts || accounts.length === 0 || numberOfMonths <= 0) {
    return {
      formattedAverage3MonthsBurn: getAmountWithCurrency({ amount: 0, currency: baseCurrency, isAccountBalance: true }),
      percentageChange: '0%',
      trend: 'default' as TrendDirection,
      valueTone: 'neutral' as const,
    };
  }

  const now = new UTCDate();

  // Collect burns for the last N completed months
  const monthlyBurns: number[] = [];
  for (let i = 1; i <= numberOfMonths; i += 1) {
    const monthAnchor = subMonths(now, i);
    const start = startOfMonth(monthAnchor);
    const end = endOfMonth(monthAnchor);
    const prevStart = startOfMonth(subMonths(now, i + 1));
    const prevEnd = endOfMonth(subMonths(now, i + 1));

    const { monthlyBurnInBaseCurrency } = getMonthlyBurnData({
      accounts,
      baseCurrency,
      currentPeriodStartDate: start,
      currentPeriodEndDate: end,
      previousPeriodStartDate: prevStart,
      previousPeriodEndDate: prevEnd,
    });

    monthlyBurns.push(monthlyBurnInBaseCurrency || 0);
  }

  const averageBurn = monthlyBurns.reduce((sum, val) => sum + val, 0) / monthlyBurns.length;
  const formattedAverage3MonthsBurn = getAmountWithCurrency({
    amount: parseFloat(averageBurn.toFixed(2)),
    currency: baseCurrency,
    isAccountBalance: true,
  });

  // Build previous sliding N-month window (shifted by one month): e.g., current Jun–Aug vs previous May–Jul
  const previousBlockBurns: number[] = [];
  for (let i = 2; i <= numberOfMonths + 1; i += 1) {
    const monthAnchor = subMonths(now, i);
    const start = startOfMonth(monthAnchor);
    const end = endOfMonth(monthAnchor);
    const prevStart = startOfMonth(subMonths(now, i + 1));
    const prevEnd = endOfMonth(subMonths(now, i + 1));

    const { monthlyBurnInBaseCurrency } = getMonthlyBurnData({
      accounts,
      baseCurrency,
      currentPeriodStartDate: start,
      currentPeriodEndDate: end,
      previousPeriodStartDate: prevStart,
      previousPeriodEndDate: prevEnd,
    });

    previousBlockBurns.push(monthlyBurnInBaseCurrency || 0);
  }

  const previousAverageBurn =
    previousBlockBurns.length > 0
      ? previousBlockBurns.reduce((sum, val) => sum + val, 0) / previousBlockBurns.length
      : 0;

  const avgAbs = Math.abs(averageBurn);
  const previousAvgAbs = Math.abs(previousAverageBurn);

  let percentageChange = '0%';
  let trend: TrendDirection = 'default';

  if (previousAvgAbs < EPSILON) {
    if (avgAbs < EPSILON) {
      percentageChange = '0%';
      trend = 'default';
    } else {
      percentageChange = '100%';
      trend = 'decreasing';
    }
  } else {
    const delta = avgAbs - previousAvgAbs; // + means worse (more burn), - means better (less burn)
    const percent = Math.abs((delta / previousAvgAbs) * 100);
    percentageChange = `${percent.toFixed(1)}%`;
    if (Math.abs(delta) < EPSILON) {
      trend = 'default';
    } else {
      trend = delta > 0 ? 'increasing' : 'decreasing';
    }
  }

  let valueTone: ValueTone;
  switch (trend) {
    case 'increasing':
      valueTone = 'negative' as const;
      break;
    case 'decreasing':
      valueTone = 'positive' as const;
      break;
    default:
      valueTone = 'neutral' as const;
      break;
  }

  return {
    formattedAverage3MonthsBurn,
    percentageChange,
    trend,
    valueTone,
  };
};
